import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, merge } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { WebSocketService } from './websocket.service';
import { AuthService } from './auth.service';

// Response DTO from API
export interface ServiceDto {
  id: string;
  name: string;
  serviceType: string;
  description?: string;
  pricingModel: string;
  price?: number;
  currency?: string;
  unitOfMeasurement?: string;
  isAvailable?: boolean;
  operatingHours?: string;
  locationDescription?: string;
  /** WGS-84, optional fixed point for the service (REST: CreateUpdateServiceDto) */
  latitude?: number;
  longitude?: number;
  contactInformation?: string;
  additionalNotes?: string;
  createdAt?: string;
  updatedAt?: string;
  marinaId: string;
  marinaName?: string;
  clientId: string;
}

// Request DTO for creating/updating services
export interface CreateUpdateServiceDto {
  name: string;
  serviceType: string;
  description?: string;
  pricingModel: string;
  price?: number;
  currency?: string;
  unitOfMeasurement?: string;
  isAvailable?: boolean;
  operatingHours?: string;
  locationDescription?: string;
  latitude?: number | null;
  longitude?: number | null;
  contactInformation?: string;
  additionalNotes?: string;
  marinaId: string;
  clientId: string;
}

// Legacy interface for backward compatibility
export interface MarinaServiceDto {
  id?: string;
  marinaId?: string;
  marinaName?: string;
  clientId?: string;
  name: string;
  description?: string;
  serviceType: string;
  pricingModel: string;
  price: number;
  currency: string;
  unitOfMeasurement?: string;
  isAvailable?: boolean;
  operatingHours?: string;
  locationDescription?: string;
  latitude?: number;
  longitude?: number;
  contactInformation?: string;
  additionalNotes?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Service type enum values
export const SERVICE_TYPES = [
  { label: 'Shower', value: 'SHOWER', icon: 'pi pi-home' },
  { label: 'Electricity', value: 'ELECTRICITY', icon: 'pi pi-bolt' },
  { label: 'WiFi', value: 'WIFI', icon: 'pi pi-wifi' },
  { label: 'Drinking Water', value: 'DRINKING_WATER', icon: 'pi pi-sun' },
  { label: 'Cafe/Restaurant', value: 'CAFE_RESTAURANT', icon: 'pi pi-th-large' },
  { label: 'Car Park', value: 'CAR_PARK', icon: 'pi pi-car' },
  { label: 'Laundry', value: 'LAUNDRY', icon: 'pi pi-sync' },
  { label: 'Toilet', value: 'TOILET', icon: 'pi pi-building' },
  { label: 'Fuel', value: 'FUEL', icon: 'pi pi-compass' },
  { label: 'Pump Out', value: 'PUMP_OUT', icon: 'pi pi-arrow-up' },
  { label: 'Waste Disposal', value: 'WASTE_DISPOSAL', icon: 'pi pi-trash' },
  { label: 'Ice', value: 'ICE', icon: 'pi pi-snowflake' },
  { label: 'Grocery Store', value: 'GROCERY_STORE', icon: 'pi pi-shopping-cart' },
  { label: 'Repair Services', value: 'REPAIR_SERVICES', icon: 'pi pi-wrench' },
  { label: 'Boat Cleaning', value: 'BOAT_CLEANING', icon: 'pi pi-sparkles' },
  { label: 'Other', value: 'OTHER', icon: 'pi pi-ellipsis-h' }
];

// Pricing model enum values
export const PRICING_MODELS = [
  { label: 'Free', value: 'FREE' },
  { label: 'Fixed Price', value: 'FIXED_PRICE' },
  { label: 'Per Hour', value: 'PER_HOUR' },
  { label: 'Per Day', value: 'PER_DAY' },
  { label: 'Per Week', value: 'PER_WEEK' },
  { label: 'Per Month', value: 'PER_MONTH' },
  { label: 'Per Unit', value: 'PER_UNIT' },
  { label: 'Per Person', value: 'PER_PERSON' },
  { label: 'Per Boat Length', value: 'PER_BOAT_LENGTH' },
  { label: 'Custom', value: 'CUSTOM' }
];

@Injectable({
  providedIn: 'root'
})
export class ServicesService {
  private apiUrl = '/api/services';

  constructor(
    private http: HttpClient,
    private router: Router,
    private webSocketService: WebSocketService,
    private authService: AuthService
  ) {
    console.log('[ServicesService] Service initialized with WebSocket support');
  }

  /**
   * Get all services
   */
  getAllServices(): Observable<ServiceDto[]> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<ServiceDto[]>(`${this.apiUrl}/list`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching all services:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get services for a specific marina
   */
  getServicesByMarinaId(marinaId: string): Observable<MarinaServiceDto[]> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<MarinaServiceDto[]>(`${this.apiUrl}/marina/${marinaId}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching services for marina:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get available services for a marina
   */
  getAvailableServicesByMarinaId(marinaId: string): Observable<MarinaServiceDto[]> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<MarinaServiceDto[]>(`${this.apiUrl}/marina/${marinaId}/available`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching available services for marina:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get services by service type
   */
  getServicesByType(serviceType: string): Observable<MarinaServiceDto[]> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<MarinaServiceDto[]>(`${this.apiUrl}/type/${serviceType}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching services by type:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get free services
   */
  getFreeServices(): Observable<MarinaServiceDto[]> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<MarinaServiceDto[]>(`${this.apiUrl}/free`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching free services:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Search services
   */
  searchServices(query: string): Observable<MarinaServiceDto[]> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<MarinaServiceDto[]>(`${this.apiUrl}/search`, { 
      headers,
      params: { query }
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error searching services:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get service by ID
   */
  getServiceById(id: string): Observable<ServiceDto> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<ServiceDto>(`${this.apiUrl}/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching service by ID:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Create a new service
   */
  createService(service: CreateUpdateServiceDto): Observable<ServiceDto> {
    const headers = this.getAuthHeaders();
    
    console.log('Creating service:', service);
    
    return this.http.post<ServiceDto>(this.apiUrl, service, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error creating service:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update an existing service
   */
  updateService(id: string, service: CreateUpdateServiceDto): Observable<ServiceDto> {
    const headers = this.getAuthHeaders();
    
    console.log('Updating service:', id, service);
    
    return this.http.put<ServiceDto>(`${this.apiUrl}/${id}`, service, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error updating service:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a service
   */
  deleteService(id: string): Observable<void> {
    const headers = this.getAuthHeaders();
    
    console.log('Deleting service:', id);
    
    return this.http.delete<void>(`${this.apiUrl}/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error deleting service:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get service type label
   */
  getServiceTypeLabel(value: string): string {
    const type = SERVICE_TYPES.find(t => t.value === value);
    return type?.label || value;
  }

  /**
   * Get pricing model label
   */
  getPricingModelLabel(value: string): string {
    const model = PRICING_MODELS.find(m => m.value === value);
    return model?.label || value;
  }

  /**
   * Get authentication headers from localStorage
   */
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('auth_token');
    const credentials = localStorage.getItem('auth_credentials');
    
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    } else if (credentials) {
      headers = headers.set('Authorization', `Basic ${credentials}`);
    }

    return headers;
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: HttpErrorResponse): void {
    if (error.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_credentials');
      localStorage.removeItem('current_user');
      this.router.navigate(['/login']);
    }
  }

  /**
   * WebSocket Integration Methods
   */

  /**
   * Get observable for real-time service updates from WebSocket (create/update)
   */
  getRealtimeServiceUpdates(): Observable<any> {
    const clientIds = this.authService.getClientIds();
    const observables = clientIds.map(clientId => 
      this.webSocketService.subscribe(`/topic/${clientId}.Service`)
    );
    return merge(...observables);
  }

  /**
   * Get observable for real-time service deletions from WebSocket
   */
  getRealtimeServiceDeletions(): Observable<any> {
    const clientIds = this.authService.getClientIds();
    const observables = clientIds.map(clientId => 
      this.webSocketService.subscribe(`/topic/${clientId}.Service`)
    );
    return merge(...observables);
  }
}

